import React, { useState, useEffect } from 'react';

const AdminRooms = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newRoom, setNewRoom] = useState({
    roomNumber: '',
    roomType: 'Standard',
    floor: '',
    pricePerNight: '',
    size: '',
    bedType: '',
    maxOccupancy: '',
    amenities: [],
    imageUrl: '',
  });

  // Fetch rooms from database
  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/rooms');
      const data = await response.json();
      
      if (data.success) {
        setRooms(data.rooms);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      alert('Error loading rooms');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available': return '#10b981';
      case 'occupied': return '#ef4444';
      case 'maintenance': return '#f59e0b';
      case 'reserved': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'available': return '✓';
      case 'occupied': return '🔒';
      case 'maintenance': return '🔧';
      case 'reserved': return '📅';
      default: return '•';
    }
  };

  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.room_number.includes(searchTerm) || room.room_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || room.status?.toLowerCase() === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: rooms.length,
    available: rooms.filter(r => r.status?.toLowerCase() === 'available').length,
    occupied: rooms.filter(r => r.status?.toLowerCase() === 'occupied').length,
    maintenance: rooms.filter(r => r.status?.toLowerCase() === 'maintenance').length,
    occupancyRate: rooms.length > 0 ? ((rooms.filter(r => r.status?.toLowerCase() === 'occupied').length / rooms.length) * 100).toFixed(1) : 0,
  };

  // Handle Add Room
  const handleAddRoom = async () => {
    if (!newRoom.roomNumber || !newRoom.pricePerNight || !newRoom.floor || !newRoom.maxOccupancy) {
      alert('Please fill all required fields');
      return;
    }

    try {
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/room', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomNumber: newRoom.roomNumber,
          roomType: newRoom.roomType,
          floor: parseInt(newRoom.floor),
          pricePerNight: parseFloat(newRoom.pricePerNight),
          size: newRoom.size || null,
          bedType: newRoom.bedType || null,
          maxOccupancy: parseInt(newRoom.maxOccupancy),
          amenities: newRoom.amenities,
          imageUrl: newRoom.imageUrl || null,
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Room added successfully!');
        setShowAddModal(false);
        setNewRoom({
          roomNumber: '',
          roomType: 'Standard',
          floor: '',
          pricePerNight: '',
          size: '',
          bedType: '',
          maxOccupancy: '',
          amenities: [],
          imageUrl: '',
        });
        fetchRooms(); // Refresh the list
      } else {
        alert(data.message || 'Error adding room');
      }
    } catch (error) {
      console.error('Error adding room:', error);
      alert('Error adding room');
    }
  };

  // Handle View Room
  const handleViewRoom = (room) => {
    setSelectedRoom(room);
    setShowViewModal(true);
  };

  // Handle Edit Room
  const handleEditRoom = (room) => {
    setSelectedRoom({ ...room });
    setShowEditModal(true);
  };

  // Handle Update Room
  const handleUpdateRoom = async () => {
    if (!selectedRoom.room_number || !selectedRoom.price_per_night || !selectedRoom.floor || !selectedRoom.max_occupancy) {
      alert('Please fill all required fields');
      return;
    }

    try {
      const response = await fetch(`https://hs.learninghub.ind.in/api/admin/room/${selectedRoom.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomNumber: selectedRoom.room_number,
          roomType: selectedRoom.room_type,
          floor: parseInt(selectedRoom.floor),
          status: selectedRoom.status,
          pricePerNight: parseFloat(selectedRoom.price_per_night),
          size: selectedRoom.size,
          bedType: selectedRoom.bed_type,
          maxOccupancy: parseInt(selectedRoom.max_occupancy),
          amenities: selectedRoom.amenities,
          imageUrl: selectedRoom.image_url,
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Room updated successfully!');
        setShowEditModal(false);
        setSelectedRoom(null);
        fetchRooms(); // Refresh the list
      } else {
        alert(data.message || 'Error updating room');
      }
    } catch (error) {
      console.error('Error updating room:', error);
      alert('Error updating room');
    }
  };

  // Handle Delete Room
  const handleDeleteRoom = (room) => {
    setSelectedRoom(room);
    setShowDeleteModal(true);
  };

  const confirmDeleteRoom = async () => {
    try {
      const response = await fetch(`https://hs.learninghub.ind.in/api/admin/room/${selectedRoom.id}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Room deleted successfully!');
        setShowDeleteModal(false);
        setSelectedRoom(null);
        fetchRooms(); // Refresh the list
      } else {
        alert(data.message || 'Error deleting room');
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      alert('Error deleting room');
    }
  };

  // Handle Amenity Toggle
  const toggleAmenity = (amenity, isNewRoom = false) => {
    if (isNewRoom) {
      setNewRoom(prev => ({
        ...prev,
        amenities: prev.amenities.includes(amenity)
          ? prev.amenities.filter(a => a !== amenity)
          : [...prev.amenities, amenity]
      }));
    } else {
      setSelectedRoom(prev => ({
        ...prev,
        amenities: prev.amenities.includes(amenity)
          ? prev.amenities.filter(a => a !== amenity)
          : [...prev.amenities, amenity]
      }));
    }
  };

  const amenitiesList = [
    '✓ Air Conditioning',
    '✓ LED TV with Cable',
    '✓ Smart TV with Premium Channels',
    '✓ Free Wi-Fi',
    '✓ High-Speed Wi-Fi',
    '✓ Premium Wi-Fi',
    '✓ Attached Bathroom',
    '✓ Attached Bathroom with Bathtub',
    '✓ Luxury Bathroom with Jacuzzi',
    '✓ Hot & Cold Water',
    '✓ Room Service',
    '✓ Room Service (24/7)',
    '✓ Daily Housekeeping',
    '✓ Daily Housekeeping (Twice)',
    '✓ Wardrobe',
    '✓ Large Wardrobe',
    '✓ Mini Fridge',
    '✓ Mini Bar',
    '✓ Tea/Coffee Maker',
    '✓ Microwave',
    '✓ Work Desk',
    '✓ Balcony',
    '✓ Private Balcony',
    '✓ Living Area',
    '✓ Complimentary Breakfast',
  ];

  const roomTypes = ['Standard', 'Deluxe', 'Suite', 'Presidential'];
  const bedTypes = ['Single Bed', 'Queen Size Bed', 'King Size Bed', 'King Size Bed + Sofa Bed', 'Twin Beds'];

  const styles = {
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '2rem',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1f2937',
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '1rem',
      color: '#6b7280',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    statCard: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      transition: 'transform 0.2s',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#1f2937',
    },
    controlsBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    searchInput: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '1rem',
      width: '300px',
      outline: 'none',
    },
    filterGroup: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
    },
    filterBtn: {
      padding: '0.75rem 1.25rem',
      borderRadius: '8px',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: '#f3f4f6',
      color: '#4b5563',
    },
    filterBtnActive: {
      background: '#667eea',
      color: 'white',
    },
    addBtn: {
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      border: 'none',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    roomsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '1.5rem',
    },
    roomCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '1.5rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s',
      cursor: 'pointer',
    },
    roomHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1rem',
    },
    roomNumber: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#1f2937',
    },
    roomType: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    statusBadge: {
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    roomDetails: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.75rem',
      marginBottom: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e5e7eb',
    },
    detailItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    },
    detailLabel: {
      fontSize: '0.75rem',
      color: '#9ca3af',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    detailValue: {
      fontSize: '0.95rem',
      fontWeight: 600,
      color: '#1f2937',
    },
    roomActions: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e5e7eb',
    },
    actionBtn: {
      flex: 1,
      padding: '0.6rem 1rem',
      borderRadius: '8px',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    viewBtn: {
      background: '#667eea',
      color: 'white',
    },
    editBtn: {
      background: '#10b981',
      color: 'white',
    },
    deleteBtn: {
      background: '#ef4444',
      color: 'white',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem',
    },
    modal: {
      background: 'white',
      borderRadius: '20px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    modalHeader: {
      padding: '2rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderRadius: '20px 20px 0 0',
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      margin: 0,
    },
    closeBtn: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      color: 'white',
      fontSize: '2rem',
      cursor: 'pointer',
      borderRadius: '8px',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background 0.2s',
      lineHeight: 1,
    },
    modalBody: {
      padding: '2rem',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#374151',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '1rem',
      outline: 'none',
      transition: 'border 0.2s',
      boxSizing: 'border-box',
    },
    select: {
      width: '100%',
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '1rem',
      outline: 'none',
      cursor: 'pointer',
      background: 'white',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '1rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    amenitiesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '0.75rem',
      marginTop: '0.75rem',
    },
    amenityCheckbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem',
      borderRadius: '8px',
      border: '2px solid',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    checkbox: {
      cursor: 'pointer',
      width: '18px',
      height: '18px',
    },
    modalActions: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'flex-end',
      marginTop: '2rem',
    },
    primaryBtn: {
      padding: '0.75rem 2rem',
      borderRadius: '8px',
      border: 'none',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s',
    },
    secondaryBtn: {
      padding: '0.75rem 2rem',
      borderRadius: '8px',
      border: 'none',
      background: '#f3f4f6',
      color: '#4b5563',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background 0.2s',
    },
    dangerBtn: {
      padding: '0.75rem 2rem',
      borderRadius: '8px',
      border: 'none',
      background: '#ef4444',
      color: 'white',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background 0.2s',
    },
    infoSection: {
      marginBottom: '1.5rem',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
    },
    infoItem: {
      padding: '1rem',
      background: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
    },
    infoLabel: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginBottom: '0.25rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    infoValue: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: '#1f2937',
    },
    amenitiesList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '0.5rem',
      marginTop: '0.75rem',
    },
    amenityItem: {
      padding: '0.6rem',
      background: '#f9fafb',
      borderRadius: '6px',
      fontSize: '0.875rem',
      color: '#4b5563',
      border: '1px solid #e5e7eb',
    },
    loading: {
      textAlign: 'center',
      padding: '3rem',
      fontSize: '1.2rem',
      color: '#6b7280',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem',
      color: '#6b7280',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
          Loading rooms...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🏨 Room Management</h1>
        <p style={styles.subtitle}>Manage hotel rooms, pricing, and availability</p>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statLabel}>Total Rooms</div>
          <div style={{ ...styles.statValue, color: '#667eea' }}>{stats.total}</div>
        </div>
        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statLabel}>Available</div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.available}</div>
        </div>
        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statLabel}>Occupied</div>
          <div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.occupied}</div>
        </div>
        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statLabel}>Maintenance</div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.maintenance}</div>
        </div>
        <div
          style={styles.statCard}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={styles.statLabel}>Occupancy Rate</div>
          <div style={{ ...styles.statValue, color: '#764ba2' }}>{stats.occupancyRate}%</div>
        </div>
      </div>

      {/* Controls Bar */}
      <div style={styles.controlsBar}>
        <input
          type="text"
          placeholder="🔍 Search rooms..."
          style={styles.searchInput}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
        />
        
        <div style={styles.filterGroup}>
          <button
            style={{
              ...styles.filterBtn,
              ...(filterStatus === 'all' ? styles.filterBtnActive : {})
            }}
            onClick={() => setFilterStatus('all')}
          >
            All
          </button>
          <button
            style={{
              ...styles.filterBtn,
              ...(filterStatus === 'available' ? styles.filterBtnActive : {})
            }}
            onClick={() => setFilterStatus('available')}
          >
            Available
          </button>
          <button
            style={{
              ...styles.filterBtn,
              ...(filterStatus === 'occupied' ? styles.filterBtnActive : {})
            }}
            onClick={() => setFilterStatus('occupied')}
          >
            Occupied
          </button>
          <button
            style={{
              ...styles.filterBtn,
              ...(filterStatus === 'maintenance' ? styles.filterBtnActive : {})
            }}
            onClick={() => setFilterStatus('maintenance')}
          >
            Maintenance
          </button>
        </div>

        <button
          style={styles.addBtn}
          onClick={() => setShowAddModal(true)}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <span style={{ fontSize: '1.2rem' }}>+</span>
          Add Room
        </button>
      </div>

      {/* Rooms Grid */}
      {filteredRooms.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
          <p>No rooms found</p>
        </div>
      ) : (
        <div style={styles.roomsGrid}>
          {filteredRooms.map((room) => (
            <div
              key={room.id}
              style={styles.roomCard}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={styles.roomHeader}>
                <div>
                  <div style={styles.roomNumber}>Room {room.room_number}</div>
                  <div style={styles.roomType}>{room.room_type}</div>
                </div>
                <div
                  style={{
                    ...styles.statusBadge,
                    background: getStatusColor(room.status),
                    color: 'white',
                  }}
                >
                  <span>{getStatusIcon(room.status)}</span>
                  {room.status}
                </div>
              </div>

              <div style={styles.roomDetails}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Floor</span>
                  <span style={styles.detailValue}>Floor {room.floor}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Capacity</span>
                  <span style={styles.detailValue}>{room.max_occupancy} Guests</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Bed Type</span>
                  <span style={styles.detailValue}>{room.bed_type || 'N/A'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Price</span>
                  <span style={styles.detailValue}>₹{room.price_per_night}/night</span>
                </div>
              </div>

              <div style={styles.roomActions}>
                <button
                  style={{ ...styles.actionBtn, ...styles.viewBtn }}
                  onClick={() => handleViewRoom(room)}
                  onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                >
                  👁️ View
                </button>
                <button
                  style={{ ...styles.actionBtn, ...styles.editBtn }}
                  onClick={() => handleEditRoom(room)}
                  onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                >
                  ✏️ Edit
                </button>
                <button
                  style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                  onClick={() => handleDeleteRoom(room)}
                  onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                  disabled={room.status?.toLowerCase() === 'occupied'}
                  title={room.status?.toLowerCase() === 'occupied' ? 'Cannot delete occupied room' : ''}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Room Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Add New Room</h2>
              <button
                style={styles.closeBtn}
                onClick={() => setShowAddModal(false)}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Number *</label>
                <input
                  type="text"
                  style={styles.input}
                  value={newRoom.roomNumber}
                  onChange={(e) => setNewRoom({ ...newRoom, roomNumber: e.target.value })}
                  placeholder="e.g., 101, 201, 301"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Room Type *</label>
                <select
                  style={styles.select}
                  value={newRoom.roomType}
                  onChange={(e) => setNewRoom({ ...newRoom, roomType: e.target.value })}
                >
                  {roomTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Floor *</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={newRoom.floor}
                    onChange={(e) => setNewRoom({ ...newRoom, floor: e.target.value })}
                    placeholder="e.g., 1, 2, 3"
                    min="1"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Max Capacity *</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={newRoom.maxOccupancy}
                    onChange={(e) => setNewRoom({ ...newRoom, maxOccupancy: e.target.value })}
                    placeholder="e.g., 2, 4"
                    min="1"
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Price per Night (₹) *</label>
                <input
                  type="number"
                  style={styles.input}
                  value={newRoom.pricePerNight}
                  onChange={(e) => setNewRoom({ ...newRoom, pricePerNight: e.target.value })}
                  placeholder="e.g., 3000, 4500"
                  min="0"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Size</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={newRoom.size}
                    onChange={(e) => setNewRoom({ ...newRoom, size: e.target.value })}
                    placeholder="e.g., 250 sq ft"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Bed Type</label>
                  <select
                    style={styles.select}
                    value={newRoom.bedType}
                    onChange={(e) => setNewRoom({ ...newRoom, bedType: e.target.value })}
                  >
                    <option value="">Select bed type</option>
                    {bedTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Amenities</label>
                <div style={styles.amenitiesGrid}>
                  {amenitiesList.map(amenity => (
                    <label
                      key={amenity}
                      style={{
                        ...styles.amenityCheckbox,
                        borderColor: newRoom.amenities.includes(amenity) ? '#667eea' : '#e5e7eb',
                        background: newRoom.amenities.includes(amenity) ? '#667eea10' : 'white',
                      }}
                    >
                      <input
                        type="checkbox"
                        style={styles.checkbox}
                        checked={newRoom.amenities.includes(amenity)}
                        onChange={() => toggleAmenity(amenity, true)}
                      />
                      <span style={{ fontSize: '0.8rem' }}>{amenity}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={styles.modalActions}>
                <button
                  style={styles.secondaryBtn}
                  onClick={() => setShowAddModal(false)}
                  onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#f3f4f6'}
                >
                  Cancel
                </button>
                <button
                  style={styles.primaryBtn}
                  onClick={handleAddRoom}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  Add Room
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Room Modal */}
      {showViewModal && selectedRoom && (
        <div style={styles.modalOverlay} onClick={() => setShowViewModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Room {selectedRoom.room_number} Details</h2>
              <button
                style={styles.closeBtn}
                onClick={() => setShowViewModal(false)}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.infoSection}>
                <div
                  style={{
                    ...styles.statusBadge,
                    background: getStatusColor(selectedRoom.status),
                    color: 'white',
                    marginBottom: '1rem',
                  }}
                >
                  <span>{getStatusIcon(selectedRoom.status)}</span>
                  {selectedRoom.status}
                </div>

                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Room Type</div>
                    <div style={styles.infoValue}>{selectedRoom.room_type}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Floor</div>
                    <div style={styles.infoValue}>Floor {selectedRoom.floor}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Capacity</div>
                    <div style={styles.infoValue}>{selectedRoom.max_occupancy} Guests</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Size</div>
                    <div style={styles.infoValue}>{selectedRoom.size || 'N/A'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Bed Type</div>
                    <div style={styles.infoValue}>{selectedRoom.bed_type || 'N/A'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Price per Night</div>
                    <div style={styles.infoValue}>₹{selectedRoom.price_per_night}</div>
                  </div>
                </div>
              </div>

              {selectedRoom.amenities && selectedRoom.amenities.length > 0 && (
                <div style={styles.infoSection}>
                  <label style={styles.label}>Amenities</label>
                  <div style={styles.amenitiesList}>
                    {selectedRoom.amenities.map((amenity, index) => (
                      <div key={index} style={styles.amenityItem}>
                        {amenity}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={styles.modalActions}>
                <button
                  style={styles.secondaryBtn}
                  onClick={() => setShowViewModal(false)}
                  onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#f3f4f6'}
                >
                  Close
                </button>
                <button
                  style={styles.primaryBtn}
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditRoom(selectedRoom);
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  Edit Room
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Room Modal */}
      {showEditModal && selectedRoom && (
        <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Edit Room {selectedRoom.room_number}</h2>
              <button
                style={styles.closeBtn}
                onClick={() => setShowEditModal(false)}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Number *</label>
                <input
                  type="text"
                  style={styles.input}
                  value={selectedRoom.room_number}
                  onChange={(e) => setSelectedRoom({ ...selectedRoom, room_number: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Room Type *</label>
                <select
                  style={styles.select}
                  value={selectedRoom.room_type}
                  onChange={(e) => setSelectedRoom({ ...selectedRoom, room_type: e.target.value })}
                >
                  {roomTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <select
                  style={styles.select}
                  value={selectedRoom.status}
                  onChange={(e) => setSelectedRoom({ ...selectedRoom, status: e.target.value })}
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="reserved">Reserved</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Floor *</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={selectedRoom.floor}
                    onChange={(e) => setSelectedRoom({ ...selectedRoom, floor: e.target.value })}
                    min="1"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Max Capacity *</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={selectedRoom.max_occupancy}
                    onChange={(e) => setSelectedRoom({ ...selectedRoom, max_occupancy: e.target.value })}
                    min="1"
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Price per Night (₹) *</label>
                <input
                  type="number"
                  style={styles.input}
                  value={selectedRoom.price_per_night}
                  onChange={(e) => setSelectedRoom({ ...selectedRoom, price_per_night: e.target.value })}
                  min="0"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Size</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={selectedRoom.size || ''}
                    onChange={(e) => setSelectedRoom({ ...selectedRoom, size: e.target.value })}
                    placeholder="e.g., 250 sq ft"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Bed Type</label>
                  <select
                    style={styles.select}
                    value={selectedRoom.bed_type || ''}
                    onChange={(e) => setSelectedRoom({ ...selectedRoom, bed_type: e.target.value })}
                  >
                    <option value="">Select bed type</option>
                    {bedTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Image URL</label>
                <input
                  type="text"
                  style={styles.input}
                  value={selectedRoom.image_url || ''}
                  onChange={(e) => setSelectedRoom({ ...selectedRoom, image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Amenities</label>
                <div style={styles.amenitiesGrid}>
                  {amenitiesList.map(amenity => (
                    <label
                      key={amenity}
                      style={{
                        ...styles.amenityCheckbox,
                        borderColor: selectedRoom.amenities?.includes(amenity) ? '#667eea' : '#e5e7eb',
                        background: selectedRoom.amenities?.includes(amenity) ? '#667eea10' : 'white',
                      }}
                    >
                      <input
                        type="checkbox"
                        style={styles.checkbox}
                        checked={selectedRoom.amenities?.includes(amenity) || false}
                        onChange={() => toggleAmenity(amenity, false)}
                      />
                      <span style={{ fontSize: '0.8rem' }}>{amenity}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={styles.modalActions}>
                <button
                  style={styles.secondaryBtn}
                  onClick={() => setShowEditModal(false)}
                  onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#f3f4f6'}
                >
                  Cancel
                </button>
                <button
                  style={styles.primaryBtn}
                  onClick={handleUpdateRoom}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  Update Room
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedRoom && (
        <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Delete Room</h2>
              <button
                style={styles.closeBtn}
                onClick={() => setShowDeleteModal(false)}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <p style={{ color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                Are you sure you want to delete <strong>Room {selectedRoom.room_number}</strong>? This action cannot be undone.
              </p>

              {selectedRoom.status?.toLowerCase() === 'occupied' && (
                <div style={{
                  padding: '1rem',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#991b1b',
                  marginBottom: '1rem',
                }}>
                  ⚠️ This room is currently occupied and cannot be deleted.
                </div>
              )}

              <div style={styles.modalActions}>
                <button
                  style={styles.secondaryBtn}
                  onClick={() => setShowDeleteModal(false)}
                  onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#f3f4f6'}
                >
                  Cancel
                </button>
                <button
                  style={styles.dangerBtn}
                  onClick={confirmDeleteRoom}
                  disabled={selectedRoom.status?.toLowerCase() === 'occupied'}
                  onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
                >
                  Delete Room
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRooms;